import axios from 'axios';
import { Auth } from 'aws-amplify';

async function getIDToken() {
    try {
        return await (await Auth.currentSession()).getIdToken().jwtToken;
    } catch (err) {
        return "";
    }
}

const instance = axios.create({
    baseURL: process.env.VUE_APP_API_URL,
    timeout: 10000,
    withCredentials: true,
});

function handleError(error, reject) {
    if ("response" in error) {
        switch (error.response.status) {
            case 422:
                reject(error.response.data);
                break;
            default:
                reject(error.response);
                break;
        }
    }
    reject(error); //TODO: Find a better way to marshall the potential errors
}

const api = {
    async createProduct(product) {
        product.metadata = JSON.stringify(product.metadata);
        var token = await getIDToken();
        return new Promise((resolve, reject) => {
            instance.post("/products", product, { headers: { "Authorization": "Bearer " + token } }).then(res => {
                res.data.metadata = JSON.parse(res.data.metadata);
                resolve(res.data);
            }).catch(err => {
                handleError(err, reject);
            });
        });
    },
    async getProduct(id) {
        return new Promise((resolve, reject) => {
            instance.get(`/products/${id}`).then(res => {
                for (var i = 0; i < res.data.products.length; i++) {
                    res.data.products[i].metadata = JSON.parse(res.data.products[i].metadata);
                }
                resolve(res.data.products[0]);
            }).catch(err => {
                handleError(err, reject);
            });
        });
    },
    async getProducts(ids) {
        return new Promise((resolve, reject) => {
            instance.get(`/products/${ids.join(";")}`).then(res => {
                for (var i = 0; i < res.data.products.length; i++) {
                    res.data.products[i].metadata = JSON.parse(res.data.products[i].metadata);
                }
                resolve(res.data);
            }).catch(err => {
                handleError(err, reject);
            });
        });
    },
    async getProductsByTypeAndStatus(type, status, page = 0, per_page = 0, metadata = "") {
        return new Promise((resolve, reject) => {
            instance.get(`/products/search?type=${type}&status=${status}&page=${page}&per_page=${per_page}&metadata=${metadata}`).then(res => {
                for (var i = 0; i < res.data.products.length; i++) {
                    res.data.products[i].metadata = JSON.parse(res.data.products[i].metadata);
                }
                resolve(res.data);
            }).catch(err => {
                handleError(err, reject);
            });
        });
    },
    async updateProduct(product) {
        product.metadata = JSON.stringify(product.metadata);
        var token = await getIDToken();
        return new Promise((resolve, reject) => {
            instance.put(`/products/${product.id}`, product, { headers: { "Authorization": "Bearer " + token } }).then(res => {
                resolve(res.data);
            }).catch(err => {
                handleError(err, reject);
            });
        });
    },
    async deleteProduct(id) {
        var token = await getIDToken();
        return new Promise((resolve, reject) => {
            instance.delete(`/products/${id}`, { headers: { "Authorization": "Bearer " + token } }).then(res => {
                resolve(res.data);
            }).catch(err => {
                handleError(err, reject);
            });
        });
    },
    async createMedia(file) {
        var token = await getIDToken();
        return new Promise((resolve, reject) => {
            var form = new FormData();
            form.append("file", file);
            instance.post("/media", form,
                {
                    headers: { "Authorization": "Bearer " + token, "Content-Type": "multipart/form-data" },
                    timeout: 60 * 1000 * 2, //2 min timeout for upload
                })
                .then(res => {
                    resolve(res.data);
                }).catch(err => {
                    handleError(err, reject);
                });
        })
    },
    async getMediaLibrary() {
        var token = await getIDToken();
        return new Promise((resolve, reject) => {
            instance.get("/media/library", { headers: { "Authorization": "Bearer " + token } }).then(res => {
                resolve(res.data);
            }).catch(err => {
                handleError(err, reject);
            });
        });
    },
    async deleteMedia(mediaID) {
        var token = await getIDToken();
        return new Promise((resolve, reject) => {
            instance.delete(`/media/${mediaID}`, { headers: { "Authorization": `Bearer ${token}` } }).then(res => {
                resolve(res.data);
            }).catch(err => {
                handleError(err, reject);
            });
        })
    },
    async createOrder(order) {
        for (var i = 0; i < order.products.length; i++) {
            order.products[i] = JSON.parse(JSON.stringify(order.products[i])); //Ensure no object refs, want clean objects
            order.products[i].metadata = JSON.stringify(order.products[i].metadata);
        }
        return new Promise((resolve, reject) => {
            instance.post("/orders", order).then(res => {
                for (var i = 0; i < res.data.products.length; i++) {
                    res.data.products[i].metadata = JSON.parse(res.data.products[i].metadata);
                }
                resolve(res.data);
            }).catch(err => {
                handleError(err, reject);
            });
        });
    },
    async getOrder(id) {
        var token = await getIDToken();
        return new Promise((resolve, reject) => {
            instance.get(`/orders/${id}`, { headers: { "Authorization": "Bearer " + token } }).then(res => {
                var order = res.data.orders[0];
                for (var i = 0; i < order.products.length; i++) {
                    order.products[i].metadata = JSON.parse(order.products[i].metadata);
                }
                resolve(order);
            }).catch(err => {
                handleError(err, reject);
            });
        });
    },
    async getOrdersByStatus(status, page = 0, per_page = 0) {
        var token = await getIDToken();
        return new Promise((resolve, reject) => {
            instance.get(`/orders/search?status=${status}&page=${page}&per_page=${per_page}`, { headers: { "Authorization": "Bearer " + token } }).then(res => {
                //TODO: If this ever comes up, need to parse the metadata for products
                resolve(res.data);
            }).catch(err => {
                handleError(err, reject);
            });
        });
    },
    async updateOrder(orderID, status, cost, address, type) {
        var token = await getIDToken();
        return new Promise((resolve, reject) => {
            instance.put(`/orders/${orderID}`,
                {
                    "status": status,
                    "shipping_cost": cost,
                    "shipping_address": address,
                    "shipping_type": type,
                }, { headers: { "Authorization": "Bearer " + token } }).then(res => {
                    //TODO: If this ever comes up, need to parse the metadata for products
                    resolve(res.data);
                }).catch(err => {
                    handleError(err, reject);
                });
        });
    },
    async updateOrderProduct(orderID, productID, status) {
        var token = await getIDToken();
        return new Promise((resolve, reject) => {
            instance.put(`/orders/${orderID}/products/${productID}`,
                {
                    "status": status,
                }, { headers: { "Authorization": "Bearer " + token } }).then(res => {
                    resolve(res.data);
                }).catch(err => {
                    handleError(err, reject);
                });
        });
    },
    async sendContactRequest(name, message, phone = "", email = "", recaptcha = "",) {
        return new Promise((resolve, reject) => {
            instance.post("/contact", {name: name, message: message, phone: phone, email: email, recaptcha: recaptcha,}).then(res => {
                resolve(res.data);
            }).catch(err => {
                handleError(err, reject);
            });
        });
    }
}

export default api; 